import React from 'react';
import { Link } from 'react-router-dom';

import {
  Collapse,
  IconButton,
  Pagination,
  TablePagination,
  TableContainer,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  Alert,
  Stack,
  Chip,
  Autocomplete,
} from '@mui/material';
import { AddCircleOutlineOutlined } from '@mui/icons-material';
import Swal from 'sweetalert2';
import { useNavigate, useLocation } from 'react-router-dom';

import { deleteRequest, getRequest, patchRequest } from 'Includes/api/apicall';
import { DEL_URL, GET_URL } from 'Includes/urls';

const stageTheme = {
  boardBg: '#eef6fb',
  boardBorder: '#9ec3d6',
  divider: '#b9d3e1',
  columnHeader: '#234a5f',
  cardBg: '#ffffff',
  cardBorder: '#dbeafe',
};

const priorityTheme = {
  low: { bg: '#58c7cf', color: '#0f3f5b' },
  medium: { bg: '#68de58', color: '#0b3f2a' },
  high: { bg: '#ff7f9c', color: '#253143' },
  critical: { bg: '#ff6e8e', color: '#253143' },
};

function TaskList(props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mainTab, setMainTab] = React.useState('list');

  const [rows, setRows] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [paginationModel, setPaginationModel] = React.useState(() => {
    const savedPage = localStorage.getItem('taskListPage');
    const savedPageSize = localStorage.getItem('taskListPageSize');
    return {
      page: savedPage ? parseInt(savedPage, 10) : 0,
      pageSize: savedPageSize ? parseInt(savedPageSize, 10) : 10,
    };
  });
  const [listWorkflowTypeId, setListWorkflowTypeId] = React.useState('');
  const [stageSearch, setStageSearch] = React.useState('');
  const [allStagesOptions, setAllStagesOptions] = React.useState([]);
  const [listAssignedTo, setListAssignedTo] = React.useState('');
  const [taskTypesOptions, setTaskTypesOptions] = React.useState([]);
  const [dashboardData, setDashboardData] = React.useState(null);
  const [dashboardLoading, setDashboardLoading] = React.useState(false);

  React.useEffect(() => {
    localStorage.setItem('taskListPage', paginationModel.page);
    localStorage.setItem('taskListPageSize', paginationModel.pageSize);
  }, [paginationModel.page, paginationModel.pageSize]);

  // Virtual option added to the top of the stage dropdown for overdue filtering
  const OVERDUE_OPTION = { id: '__overdue__', name: 'Overdue / Deadline' };

  const uniqueStagesOptions = React.useMemo(() => {
    const seen = new Set();
    const list = [];
    allStagesOptions.forEach((s) => {
      const nameLower = String(s.name).toLowerCase();
      if (!seen.has(nameLower) && s.name) {
        seen.add(nameLower);
        list.push(s);
      }
    });
    // Prepend the special overdue option at the top
    return [OVERDUE_OPTION, ...list];
  }, [allStagesOptions]);

  const [kanbanTaskTypeId, setKanbanTaskTypeId] = React.useState('');
  const [kanbanStagesAll, setKanbanStagesAll] = React.useState([]);
  const [kanbanSelectedStageIds, setKanbanSelectedStageIds] = React.useState([]);
  const [kanbanTasks, setKanbanTasks] = React.useState([]);
  const [kanbanLoading, setKanbanLoading] = React.useState(false);
  const [kanbanPaginationModel, setKanbanPaginationModel] = React.useState({ page: 0, pageSize: 50 });
  const [kanbanRowCount, setKanbanRowCount] = React.useState(0);
  const [draggingTaskId, setDraggingTaskId] = React.useState(null);
  const [hoverStageId, setHoverStageId] = React.useState('');
  const [kanbanAssignedTo, setKanbanAssignedTo] = React.useState('');

  const [assignDialog, setAssignDialog] = React.useState({ open: false, taskId: null, assigned_to: '' });
  const [historyDialog, setHistoryDialog] = React.useState({
    open: false,
    taskTitle: '',
    loading: false,
    fromDate: null,
    toDate: null,
    perUser: [],
    assignmentHistory: [],
  });

  React.useEffect(() => {
    getRequest(GET_URL.user.api, {}, props).then((response) => {
      if (response && response.status === 200) {
        setUsers(response.data?.data || []);
      }
    });
  }, [props]);
  React.useEffect(() => {
    getRequest(GET_URL.task_types.api, { limit: 200, pageno: 1 }, props).then((response) => {
      if (response && response.status === 200) {
        setTaskTypesOptions(response.data?.data || []);
      }
    });
  }, [props]);

  React.useEffect(() => {
    getRequest(GET_URL.task_type_stages.api, { limit: 1000, pageno: 1 }, props).then((response) => {
      if (response && response.status === 200) {
        setAllStagesOptions(response.data?.data || []);
      }
    });
  }, [props]);

  // Runs every time the URL changes (dashboard card clicks) or stages load
  React.useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const statusParam = urlParams.get('status');

    if (!statusParam) {
      // No status param (e.g. "Total Tasks" card) → clear filter
      setStageSearch('');
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
      return;
    }

    const normalized = statusParam.replace(/[-_\s]/g, '').toLowerCase();

    if (normalized === 'overdue') {
      setStageSearch('Overdue / Deadline');
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    } else {
      // Find the best matching stage name from loaded stages
      // e.g. 'InProgress' should match 'In Progress', 'in-progress', 'inprogress'
      if (allStagesOptions.length > 0) {
        const matched = allStagesOptions.find(
          (s) => s.name.replace(/[-_\s]/g, '').toLowerCase() === normalized
        );
        // Use matched stage name (exact), otherwise use the raw param as fallback
        setStageSearch(matched ? matched.name : statusParam);
      } else {
        // Stages not yet loaded — store raw param, loadData will handle matching
        setStageSearch(statusParam);
      }
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
  }, [location.search, allStagesOptions]);


  React.useEffect(() => {
    if (!kanbanTaskTypeId) {
      setKanbanStagesAll([]);
      setKanbanSelectedStageIds([]);
      return;
    }
    getRequest(
      GET_URL.task_type_stages.api,
      { limit: 500, pageno: 1, task_type: kanbanTaskTypeId },
      props
    ).then((response) => {
      if (response && response.status === 200) {
        const stages = response.data?.data || [];
        setKanbanStagesAll(stages);
        setKanbanSelectedStageIds(stages.map((s) => String(s.id)));
      }
    });
  }, [kanbanTaskTypeId, props]);

  const kanbanFiltersReady = Boolean(kanbanTaskTypeId) && kanbanSelectedStageIds.length > 0;

  const loadData = React.useCallback(() => {
    setLoading(true);
    const trimmed = stageSearch.trim();
    const isOverdueSelected = trimmed === 'Overdue / Deadline';

    if (isOverdueSelected) {
      // Client-side overdue filtering: fetch all tasks and filter by deadline < today
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const params = {
        limit: 1000,
        pageno: 1,
        ...(listWorkflowTypeId ? { task_type: listWorkflowTypeId } : {}),
        ...(listAssignedTo ? { assigned_to: listAssignedTo } : {}),
      };
      getRequest(GET_URL.tasks.api, params, props)
        .then((response) => {
          if (response && response.status === 200) {
            const allTasks = response.data?.data || [];
            const overdueTasks = allTasks.filter((task) => {
              if (!task.deadline) return false;
              const deadlineDate = new Date(task.deadline);
              deadlineDate.setHours(0, 0, 0, 0);
              return deadlineDate < today;
            });
            setRows(overdueTasks);
            setRowCount(overdueTasks.length);
          }
          setLoading(false);
        })
        .catch(() => setLoading(false));
      return;
    }

    let resolvedTaskStages = '';
    if (trimmed) {
      // Flexible match: normalise both sides by removing spaces/dashes
      // so 'InProgress' (from URL) matches 'In Progress' (stage name in DB)
      const normalizedSearch = trimmed.replace(/[-_\s]/g, '').toLowerCase();
      const matches = allStagesOptions.filter((s) =>
        s.name.replace(/[-_\s]/g, '').toLowerCase().includes(normalizedSearch) ||
        normalizedSearch.includes(s.name.replace(/[-_\s]/g, '').toLowerCase())
      );
      resolvedTaskStages = matches.length > 0 ? matches.map((m) => String(m.id)).join(',') : '-1';
    }
    const params = {
      limit: paginationModel.pageSize,
      pageno: paginationModel.page + 1,
      ...(listWorkflowTypeId ? { task_type: listWorkflowTypeId } : {}),
      ...(listAssignedTo ? { assigned_to: listAssignedTo } : {}),
      ...(resolvedTaskStages ? { task_stages: resolvedTaskStages } : {}),
    };
    getRequest(GET_URL.tasks.api, params, props)
      .then((response) => {
        if (response && response.status === 200) {
          setRows(response.data?.data || []);
          setRowCount(response.data?.count || 0);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [paginationModel.page, paginationModel.pageSize, props, listWorkflowTypeId, stageSearch, allStagesOptions, listAssignedTo]);

  const loadDashboardSummary = React.useCallback(() => {
    setDashboardLoading(true);
    getRequest(GET_URL.tasks.api, { dashboard_summary: 1 }, props)
      .then((response) => {
        if (response && response.status === 200) {
          setDashboardData(response.data?.data || null);
        }
        setDashboardLoading(false);
      })
      .catch(() => setDashboardLoading(false));
  }, [props]);

  React.useEffect(() => {
    if (mainTab === 'summary') {
      loadDashboardSummary();
    }
  }, [mainTab, loadDashboardSummary]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const loadKanbanTasks = React.useCallback(() => {
    if (!kanbanFiltersReady) {
      setKanbanTasks([]);
      return;
    }
    setKanbanLoading(true);
    const params = {
      limit: kanbanPaginationModel.pageSize,
      pageno: kanbanPaginationModel.page + 1,
      task_type: kanbanTaskTypeId,
      task_stages: kanbanSelectedStageIds.join(','),
      ...(kanbanAssignedTo ? { assigned_to: kanbanAssignedTo } : {}),
    };
    getRequest(GET_URL.tasks.api, params, props)
      .then((response) => {
        if (response && response.status === 200) {
          setKanbanTasks(response.data?.data || []);
          setKanbanRowCount(response.data?.count || 0);
        }
        setKanbanLoading(false);
      })
      .catch(() => setKanbanLoading(false));
  }, [props, kanbanFiltersReady, kanbanTaskTypeId, kanbanSelectedStageIds, kanbanAssignedTo, kanbanPaginationModel.page, kanbanPaginationModel.pageSize]);

  React.useEffect(() => {
    if (mainTab === 'kanban') {
      loadKanbanTasks();
    }
  }, [mainTab, loadKanbanTasks]);

  const visibleKanbanColumns = React.useMemo(() => {
    const sel = new Set(kanbanSelectedStageIds.map(String));
    return [...kanbanStagesAll]
      .filter((s) => sel.has(String(s.id)))
      .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0));
  }, [kanbanStagesAll, kanbanSelectedStageIds]);

  const kanbanBoard = React.useMemo(() => {
    const grouped = {};
    visibleKanbanColumns.forEach((col) => {
      grouped[String(col.id)] = [];
    });
    (kanbanTasks || []).forEach((task) => {
      const sid = task.task_stage != null ? String(task.task_stage) : '';
      if (sid && grouped[sid]) {
        grouped[sid].push(task);
      }
    });
    return grouped;
  }, [kanbanTasks, visibleKanbanColumns]);

  const onDelete = (id) => {
    deleteRequest(`${DEL_URL.tasks.api}${id}/`, {}, props).then(() => loadData());
  };

  const openAssignDialog = (row) => {
    setAssignDialog({ open: true, taskId: row.id, assigned_to: row.assigned_to || '' });
  };

  const saveAssignment = () => {
    if (!assignDialog.taskId) return;
    patchRequest(`${GET_URL.tasks.api}${assignDialog.taskId}/`, { assigned_to: assignDialog.assigned_to || null }, props).then(() => {
      setAssignDialog({ open: false, taskId: null, assigned_to: '' });
      Swal.fire({ icon: 'success', title: 'Task assignment updated', timer: 1200, showConfirmButton: false });
      loadData();
      loadKanbanTasks();
    });
  };

  const openTaskHistory = (row) => {
    setHistoryDialog({
      open: true,
      taskTitle: row.title,
      loading: true,
      fromDate: null,
      toDate: null,
      perUser: [],
      assignmentHistory: [],
    });

    Promise.all([
      getRequest(`${GET_URL.tasks.api}${row.id}/`, { time_summary: 1 }, props),
      getRequest(`${GET_URL.tasks.api}${row.id}/`, { assignment_history: 1 }, props),
    ])
      .then(([timeResponse, assignResponse]) => {
        const payload = (timeResponse && timeResponse.status === 200) ? (timeResponse.data?.data || {}) : {};
        const logs = payload.logs || [];
        const dates = logs
          .map((log) => log.work_date)
          .filter(Boolean)
          .sort();
        const assignments = (assignResponse && assignResponse.status === 200) ? (assignResponse.data?.data || []) : [];

        setHistoryDialog((prev) => ({
          ...prev,
          loading: false,
          fromDate: dates.length ? dates[0] : null,
          toDate: dates.length ? dates[dates.length - 1] : null,
          perUser: payload.per_user || [],
          assignmentHistory: assignments,
        }));
      })
      .catch(() => {
        setHistoryDialog((prev) => ({ ...prev, loading: false }));
        Swal.fire({ icon: 'error', title: 'Unable to load task history' });
      });
  };

  const onTaskDragStart = (event, taskId) => {
    setDraggingTaskId(taskId);
    event.dataTransfer.setData('text/plain', String(taskId));
    event.dataTransfer.effectAllowed = 'move';
  };

  const onStageDrop = (event, stageId) => {
    event.preventDefault();
    const droppedTaskId = Number(event.dataTransfer.getData('text/plain') || draggingTaskId);
    setHoverStageId('');
    setDraggingTaskId(null);
    if (!droppedTaskId || !stageId) return;

    const droppedTask = (kanbanTasks || []).find((row) => row.id === droppedTaskId);
    if (!droppedTask || String(droppedTask.task_stage) === String(stageId)) return;

    const prevStage = droppedTask.task_stage;
    setKanbanTasks((prev) =>
      prev.map((row) => (row.id === droppedTaskId ? { ...row, task_stage: stageId } : row))
    );

    patchRequest(`${GET_URL.tasks.api}${droppedTaskId}/`, { task_stage: stageId }, props).catch(() => {
      setKanbanTasks((prev) =>
        prev.map((row) => (row.id === droppedTaskId ? { ...row, task_stage: prevStage } : row))
      );
      Swal.fire({ icon: 'error', title: 'Unable to update task stage' });
    });
  };


  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Task List</Typography>
          <Typography variant="body2" color="text.secondary">List view with assignment and deadline tracking, or Kanban by workflow type and stages.</Typography>
        </Box>
        <Button variant="contained" component={Link} to="/task" startIcon={<AddCircleOutlineOutlined />} sx={{ textTransform: 'none' }}>
          Add Task
        </Button>
      </Box>

      <Tabs value={mainTab} onChange={(_, v) => setMainTab(v)} sx={{ mt: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="list" label="List" sx={{ textTransform: 'none' }} />
        <Tab value="kanban" label="Kanban" sx={{ textTransform: 'none' }} />
        <Tab value="summary" label="Summary" sx={{ textTransform: 'none' }} />
      </Tabs>

      {mainTab === 'list' && (
        <>
          <Paper sx={{ mt: 2, p: 2, borderRadius: 2, mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  size="small"
                  options={uniqueStagesOptions}
                  getOptionLabel={(option) => option.name || ''}
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                  value={uniqueStagesOptions.find((s) => String(s.name).toLowerCase() === String(stageSearch).toLowerCase()) || null}
                  inputValue={stageSearch}
                  onInputChange={(_, newInputValue, reason) => {
                    if (reason === 'input' || reason === 'clear') {
                      setStageSearch(newInputValue);
                      setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }
                  }}
                  onChange={(_, selected) => {
                    setStageSearch(selected ? selected.name : '');
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Search Stage" placeholder="Type stage name..." />
                  )}
                  ListboxProps={{ style: { maxHeight: 300 } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="workflow-type-filter-label">Task type</InputLabel>
                  <Select
                    labelId="workflow-type-filter-label"
                    label="Task type"
                    value={listWorkflowTypeId}
                    onChange={(e) => {
                      setListWorkflowTypeId(e.target.value);
                      setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                  >
                    <MenuItem value="">
                      <em>All types</em>
                    </MenuItem>
                    {taskTypesOptions.map((t) => (
                      <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="assigned-to-filter-label">Assigned To</InputLabel>
                  <Select
                    labelId="assigned-to-filter-label"
                    label="Assigned To"
                    value={listAssignedTo}
                    onChange={(e) => {
                      setListAssignedTo(e.target.value);
                      setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                  >
                    <MenuItem value="">
                      <em>All Assignees</em>
                    </MenuItem>
                    {users.map((u) => (
                      <MenuItem key={u.id} value={String(u.id)}>{u.username}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell><strong>Task</strong></TableCell>
                    <TableCell><strong>Task type</strong></TableCell>
                    <TableCell><strong>Stage</strong></TableCell>
                    <TableCell><strong>Priority</strong></TableCell>
                    <TableCell><strong>Sprint</strong></TableCell>
                    <TableCell><strong>Deadline</strong></TableCell>
                    <TableCell><strong>Assigned To</strong></TableCell>
                    <TableCell><strong>Created By</strong></TableCell>
                    <TableCell><strong>Created Date</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <ExpandableRow
                      key={row.id}
                      row={row}
                      onEdit={() => navigate(`/task?task_id=${row.id}`)}
                      onDelete={() => onDelete(row.id)}
                      taskTypesOptions={taskTypesOptions}
                      users={users}
                      props={props}
                      loadData={loadData}
                      loadKanbanTasks={loadKanbanTasks}
                    />
                  ))}
                  {rows.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={11} align="center">No tasks found</TableCell>
                    </TableRow>
                  )}
                  {loading && (
                    <TableRow>
                      <TableCell colSpan={11} align="center"><CircularProgress size={24} /></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={rowCount}
              page={paginationModel.page}
              rowsPerPage={paginationModel.pageSize}
              rowsPerPageOptions={[5, 10, 25, 50]}
              onPageChange={(_, newPage) => setPaginationModel((prev) => ({ ...prev, page: newPage }))}
              onRowsPerPageChange={(e) => setPaginationModel({ page: 0, pageSize: parseInt(e.target.value, 10) })}
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count} tasks`}
              sx={{ mt: 1 }}
            />
          </Paper>
          </Paper>
        </>
      )}

      {mainTab === 'kanban' && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Select a workflow task type and at least one stage to load the board. Both choices are required.
          </Alert>
          <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small" required error={!kanbanTaskTypeId}>
                  <InputLabel id="kanban-tt-label">Task type</InputLabel>
                  <Select
                    labelId="kanban-tt-label"
                    label="Task type"
                    value={kanbanTaskTypeId}
                    required
                    onChange={(e) => {
                      setKanbanTaskTypeId(e.target.value);
                    }}
                  >
                    <MenuItem value="">
                      <em>Select task type</em>
                    </MenuItem>
                    {taskTypesOptions.map((t) => (
                      <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small" required error={kanbanSelectedStageIds.length === 0}>
                  <InputLabel id="kanban-stages-label">Stages</InputLabel>
                  <Select
                    labelId="kanban-stages-label"
                    label="Stages"
                    multiple
                    value={kanbanSelectedStageIds}
                    disabled={!kanbanTaskTypeId || !kanbanStagesAll.length}
                    onChange={(e) => {
                      const v = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                      setKanbanSelectedStageIds(v);
                    }}
                    renderValue={(selected) => {
                      const labels = kanbanStagesAll
                        .filter((s) => selected.includes(String(s.id)))
                        .map((s) => s.name);
                      return labels.join(', ') || 'Select stages';
                    }}
                  >
                    {kanbanStagesAll.map((s) => (
                      <MenuItem key={s.id} value={String(s.id)}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="kanban-assigned-to-label">Assigned To</InputLabel>
                  <Select
                    labelId="kanban-assigned-to-label"
                    label="Assigned To"
                    value={kanbanAssignedTo}
                    onChange={(e) => {
                      setKanbanAssignedTo(e.target.value);
                      setKanbanPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                  >
                    <MenuItem value="">
                      <em>All Assignees</em>
                    </MenuItem>
                    {users.map((u) => (
                      <MenuItem key={u.id} value={String(u.id)}>{u.username}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {!kanbanFiltersReady && (
            <Typography variant="body2" color="text.secondary">
              Choose a task type and one or more stages to view the Kanban board.
            </Typography>
          )}

          {kanbanFiltersReady && kanbanLoading && (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={32} />
            </Box>
          )}

          {kanbanFiltersReady && !kanbanLoading && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'nowrap',
                gap: 0,
                overflowX: 'auto',
                bgcolor: stageTheme.boardBg,
                border: '1px solid',
                borderColor: stageTheme.boardBorder,
                borderRadius: 4,
                p: 2,
              }}
            >
              {visibleKanbanColumns.map((column, idx) => (
                <Box
                  key={column.id}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setHoverStageId(String(column.id));
                  }}
                  onDragLeave={() => setHoverStageId('')}
                  onDrop={(event) => onStageDrop(event, column.id)}
                  sx={{
                    flex: '1 1 240px',
                    minWidth: 240,
                    maxWidth: 400,
                    borderRight:
                      idx < visibleKanbanColumns.length - 1 ? `1px solid ${stageTheme.divider}` : 'none',
                    px: 2,
                    py: 1,
                    minHeight: 470,
                    bgcolor: hoverStageId === String(column.id) ? '#e0f2fe' : 'transparent',
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <Box sx={{ p: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        fontWeight: 800,
                        color: stageTheme.columnHeader,
                        fontSize: '1rem',
                      }}
                    >
                      {column.name}
                    </Typography>
                    {(kanbanBoard[String(column.id)] || []).map((task) => (
                      <Paper
                        key={task.id}
                        variant="outlined"
                        draggable
                        onDragStart={(event) => onTaskDragStart(event, task.id)}
                        sx={{
                          p: 1.2,
                          mb: 1.5,
                          ml: 0.5,
                          mr: 0.5,
                          bgcolor: priorityTheme[task.priority]?.bg || stageTheme.cardBg,
                          borderRadius: 1.5,
                          borderColor: priorityTheme[task.priority]?.bg || stageTheme.cardBorder,
                          minHeight: 96,
                          cursor: 'grab',
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                          <Typography variant="body2" fontWeight={700} sx={{ mb: 0.8, pr: 1 }}>
                            {task.title}
                          </Typography>
                        </Stack>
                        <Box sx={{ mt: 0.8, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          <Chip
                            label={(task.priority || '').toUpperCase()}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              bgcolor: 'rgba(255,255,255,0.6)',
                              color: priorityTheme[task.priority]?.color || '#374151',
                            }}
                          />
                          <Chip label={task.task_type_name || task.category || ''} size="small" variant="outlined" />
                        </Box>
                        <Typography variant="caption" display="block" sx={{ mt: 0.8 }}>
                          {task.assigned_to_name || 'Unassigned'}
                        </Typography>
                        <Typography variant="caption" display="block">
                          {task.sprint_name || 'No sprint'}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {kanbanFiltersReady && !kanbanLoading && kanbanRowCount > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Pagination
                count={Math.ceil(kanbanRowCount / kanbanPaginationModel.pageSize)}
                page={kanbanPaginationModel.page + 1}
                onChange={(_, newPage) => setKanbanPaginationModel((prev) => ({ ...prev, page: newPage - 1 }))}
                color="primary"
              />
            </Box>
          )}
        </Box>
      )}

      {mainTab === 'summary' && (
        <Box sx={{ mt: 2 }}>
          {dashboardLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          )}

          {!dashboardLoading && dashboardData && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Paper
                  sx={{
                    p: 2.5,
                    borderRadius: 2.5,
                    boxShadow: '0 4px 12px rgba(16, 24, 40, 0.02)',
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #f1f3f5 100%)',
                    border: '1px solid #e9ecef',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="h4" fontWeight={800} color="#212529">
                    {dashboardData.meta?.total_tasks || 0}
                  </Typography>
                  <Typography variant="caption" fontWeight={700} color="#495057" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', mt: 0.5, display: 'block', letterSpacing: 0.5 }}>
                    Total Tasks
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Paper
                  sx={{
                    p: 2.5,
                    borderRadius: 2.5,
                    boxShadow: '0 4px 12px rgba(16, 24, 40, 0.02)',
                    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                    border: '1px solid #bfdbfe',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="h4" fontWeight={800} color="#1d4ed8">
                    {(dashboardData.meta?.total_tasks || 0) - ((dashboardData.stage_summary || []).find(item => item.name.toLowerCase() === 'done')?.count || 0)}
                  </Typography>
                  <Typography variant="caption" fontWeight={700} color="#1e40af" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', mt: 0.5, display: 'block', letterSpacing: 0.5 }}>
                    Open Tasks
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Paper
                  sx={{
                    p: 2.5,
                    borderRadius: 2.5,
                    boxShadow: '0 4px 12px rgba(16, 24, 40, 0.02)',
                    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                    border: '1px solid #bbf7d0',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="h4" fontWeight={800} color="#15803d">
                    {(dashboardData.stage_summary || []).find(item => item.name.toLowerCase() === 'done')?.count || 0}
                  </Typography>
                  <Typography variant="caption" fontWeight={700} color="#166534" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', mt: 0.5, display: 'block', letterSpacing: 0.5 }}>
                    Closed Tasks
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Stage Breakdown</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Stage Code</strong></TableCell>
                        <TableCell align="right"><strong>Count</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(dashboardData.stage_summary || []).map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ textTransform: 'capitalize' }}>{row.name}</TableCell>
                          <TableCell align="right">{row.count}</TableCell>
                        </TableRow>
                      ))}
                      {(!dashboardData.stage_summary || dashboardData.stage_summary.length === 0) && (
                        <TableRow><TableCell colSpan={2} align="center">No data available</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Priority Breakdown</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Priority</strong></TableCell>
                        <TableCell align="right"><strong>Count</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(dashboardData.priority_summary || []).map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ textTransform: 'capitalize' }}>{row.name}</TableCell>
                          <TableCell align="right">{row.count}</TableCell>
                        </TableRow>
                      ))}
                      {(!dashboardData.priority_summary || dashboardData.priority_summary.length === 0) && (
                        <TableRow><TableCell colSpan={2} align="center">No data available</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Sprint Breakdown</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Sprint</strong></TableCell>
                        <TableCell align="right"><strong>Count</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(dashboardData.sprint_summary || []).map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{row.name}</TableCell>
                          <TableCell align="right">{row.count}</TableCell>
                        </TableRow>
                      ))}
                      {(!dashboardData.sprint_summary || dashboardData.sprint_summary.length === 0) && (
                        <TableRow><TableCell colSpan={2} align="center">No data available</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Category Breakdown</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Category</strong></TableCell>
                        <TableCell align="right"><strong>Count</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(dashboardData.category_summary || []).map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ textTransform: 'capitalize' }}>{row.name}</TableCell>
                          <TableCell align="right">{row.count}</TableCell>
                        </TableRow>
                      ))}
                      {(!dashboardData.category_summary || dashboardData.category_summary.length === 0) && (
                        <TableRow><TableCell colSpan={2} align="center">No data available</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
            </Grid>
          )}
        </Box>
      )}

      <Dialog open={assignDialog.open} onClose={() => setAssignDialog({ open: false, taskId: null, assigned_to: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>Assign Task</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            select
            size="small"
            label="Assigned To"
            value={assignDialog.assigned_to}
            onChange={(e) => setAssignDialog((prev) => ({ ...prev, assigned_to: e.target.value }))}
            sx={{ mt: 1 }}
          >
            <MenuItem value="">Unassigned</MenuItem>
            {users.map((user) => (
              <MenuItem key={user.id} value={user.id}>{user.username}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog({ open: false, taskId: null, assigned_to: '' })}>Cancel</Button>
          <Button variant="contained" onClick={saveAssignment}>Save</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={historyDialog.open}
        onClose={() => setHistoryDialog({ open: false, taskTitle: '', loading: false, fromDate: null, toDate: null, perUser: [], assignmentHistory: [] })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Task History - {historyDialog.taskTitle}</DialogTitle>
        <DialogContent>
          {historyDialog.loading ? (
            <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={26} />
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  From: {historyDialog.fromDate || 'NA'} | To: {historyDialog.toDate || 'NA'}
                </Typography>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Worked By</strong></TableCell>
                    <TableCell align="right"><strong>Total Minutes</strong></TableCell>
                    <TableCell align="right"><strong>Total Hours</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyDialog.perUser.length ? historyDialog.perUser.map((entry) => (
                    <TableRow key={entry.worked_by}>
                      <TableCell>{entry.worked_by_name || 'Unknown'}</TableCell>
                      <TableCell align="right">{entry.total_minutes || 0}</TableCell>
                      <TableCell align="right">{entry.total_hours || 0}</TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={3} align="center">No work logs found for this task.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
                Assignment History
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Previous Assignee</strong></TableCell>
                    <TableCell><strong>New Assignee</strong></TableCell>
                    <TableCell><strong>Changed By</strong></TableCell>
                    <TableCell><strong>Changed At</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyDialog.assignmentHistory.length ? historyDialog.assignmentHistory.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>{entry.previous_assignee_name || 'Unassigned'}</TableCell>
                      <TableCell>{entry.new_assignee_name || 'Unassigned'}</TableCell>
                      <TableCell>{entry.changed_by_name || 'Unknown'}</TableCell>
                      <TableCell>{entry.changed_at || 'NA'}</TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">No assignment history found for this task.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialog({ open: false, taskTitle: '', loading: false, fromDate: null, toDate: null, perUser: [], assignmentHistory: [] })}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TaskList;


function ExpandableRow({ row, onEdit, onDelete, taskTypesOptions, users, props, loadData, loadKanbanTasks }) {
  const [open, setOpen] = React.useState(false);
  const [tab, setTab] = React.useState('status');
  
  // Status tab state
  const [stages, setStages] = React.useState([]);
  const [selectedStage, setSelectedStage] = React.useState(row.task_stage ? String(row.task_stage) : '');

  // Assign tab state
  const [assignedTo, setAssignedTo] = React.useState(row.assigned_to || '');

  // Comments state
  const [commentText, setCommentText] = React.useState('');
  const [comments, setComments] = React.useState(row.details?.comments || []);
  const [commentsLoading, setCommentsLoading] = React.useState(false);

  // History state
  const [historyLoaded, setHistoryLoaded] = React.useState(false);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const [perUser, setPerUser] = React.useState([]);
  const [assignmentHistory, setAssignmentHistory] = React.useState([]);

  React.useEffect(() => {
    if (open && row.task_type) {
      getRequest(GET_URL.task_type_stages.api, { limit: 500, pageno: 1, task_type: row.task_type }, props).then((response) => {
        if (response && response.status === 200) {
          setStages(response.data?.data || []);
        }
      });
    }
  }, [open, row.task_type, props]);

  const loadHistory = () => {
    setHistoryLoading(true);
    Promise.all([
      getRequest(`${GET_URL.tasks.api}${row.id}/`, { time_summary: 1 }, props),
      getRequest(`${GET_URL.tasks.api}${row.id}/`, { assignment_history: 1 }, props),
    ]).then(([timeRes, assignRes]) => {
      const pData = timeRes?.data?.data || {};
      setPerUser(pData.per_user || []);
      setAssignmentHistory(assignRes?.data?.data || []);
      setHistoryLoading(false);
      setHistoryLoaded(true);
    }).catch(() => setHistoryLoading(false));
  };

  React.useEffect(() => {
    if (open && tab === 'history' && !historyLoaded) {
      loadHistory();
    }
  }, [open, tab, historyLoaded]);

  const saveStage = () => {
    patchRequest(`${GET_URL.tasks.api}${row.id}/`, { task_stage: selectedStage || null }, props).then(() => {
      Swal.fire({ icon: 'success', title: 'Stage updated', timer: 1200, showConfirmButton: false });
      loadData();
      loadKanbanTasks();
    });
  };

  const saveAssign = () => {
    patchRequest(`${GET_URL.tasks.api}${row.id}/`, { assigned_to: assignedTo || null }, props).then(() => {
      Swal.fire({ icon: 'success', title: 'Assignment updated', timer: 1200, showConfirmButton: false });
      loadData();
      loadKanbanTasks();
    });
  };

  const saveComment = () => {
    if (!commentText.trim()) return;
    setCommentsLoading(true);
    const newComment = {
      username: JSON.parse(localStorage.getItem('user') || '{}').username || 'User',
      comment: commentText,
      date: new Date().toISOString()
    };
    const updatedDetails = { ...(row.details || {}), comments: [newComment, ...(comments)] };
    patchRequest(`${GET_URL.tasks.api}${row.id}/`, { details: updatedDetails }, props).then(() => {
      setComments([newComment, ...comments]);
      setCommentText('');
      setCommentsLoading(false);
      loadData(); // To update the row in main state if needed
    }).catch(() => setCommentsLoading(false));
  };

  return (
    <React.Fragment>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{row.title}</TableCell>
        <TableCell>{row.task_type_name}</TableCell>
        <TableCell>{row.task_stage_name}</TableCell>
        <TableCell>{row.priority}</TableCell>
        <TableCell>{row.sprint_name}</TableCell>
        <TableCell>{row.deadline}</TableCell>
        <TableCell>{row.assigned_to_name || 'Unassigned'}</TableCell>
        <TableCell>{row.created_by_name}</TableCell>
        <TableCell>
          {row.created
            ? new Date(row.created).toLocaleString('en-IN', {
                day: '2-digit', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit', hour12: true,
              })
            : '-'}
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="outlined" onClick={onEdit}>Edit</Button>
            <Button size="small" color="error" variant="outlined" onClick={onDelete}>Delete</Button>
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1, p: 2, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#fafafa' }}>
              <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, minHeight: 32 }} TabIndicatorProps={{ style: { display: 'none' } }}>
                <Tab label="Status" value="status" sx={{ minHeight: 32, py: 0.5, borderRadius: 1, '&.Mui-selected': { bgcolor: '#e3f2fd' } }} />
                <Tab label="Comments" value="comments" sx={{ minHeight: 32, py: 0.5, borderRadius: 1, '&.Mui-selected': { bgcolor: '#e3f2fd' } }} />
                <Tab label="Work Log (History)" value="history" sx={{ minHeight: 32, py: 0.5, borderRadius: 1, '&.Mui-selected': { bgcolor: '#e3f2fd' } }} />
                <Tab label="Assign" value="assign" sx={{ minHeight: 32, py: 0.5, borderRadius: 1, '&.Mui-selected': { bgcolor: '#e3f2fd' } }} />
              </Tabs>
              
              {tab === 'status' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Stage</InputLabel>
                    <Select value={selectedStage} label="Stage" onChange={(e) => setSelectedStage(e.target.value)}>
                      {stages.map(s => <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Button variant="contained" size="small" onClick={saveStage}>Save Stage</Button>
                </Box>
              )}

              {tab === 'assign' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Assigned To</InputLabel>
                    <Select value={assignedTo} label="Assigned To" onChange={(e) => setAssignedTo(e.target.value)}>
                      <MenuItem value="">Unassigned</MenuItem>
                      {users.map((user) => (
                        <MenuItem key={user.id} value={user.id}>{user.username}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button variant="contained" size="small" onClick={saveAssign}>Save Assign</Button>
                </Box>
              )}

              {tab === 'comments' && (
                <Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <TextField 
                      fullWidth 
                      size="small" 
                      placeholder="Add a comment..." 
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                    />
                    <Button variant="contained" size="small" disabled={commentsLoading || !commentText.trim()} onClick={saveComment}>Save</Button>
                  </Box>
                  <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                    {comments.map((c, i) => (
                      <Paper key={i} elevation={0} sx={{ p: 1.5, mb: 1, border: '1px solid #e0e0e0' }}>
                        <Typography variant="caption" fontWeight="bold" color="primary">{c.username}</Typography>
                        <Typography variant="caption" sx={{ ml: 2 }} color="text.secondary">{new Date(c.date).toLocaleString()}</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>{c.comment}</Typography>
                      </Paper>
                    ))}
                    {comments.length === 0 && <Typography variant="body2" color="text.secondary">No comments yet.</Typography>}
                  </Box>
                </Box>
              )}

              {tab === 'history' && (
                <Box>
                  {historyLoading ? <CircularProgress size={24} /> : (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Work Logs</Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow><TableCell>User</TableCell><TableCell align="right">Minutes</TableCell></TableRow>
                          </TableHead>
                          <TableBody>
                            {perUser.map(u => (
                              <TableRow key={u.worked_by}>
                                <TableCell>{u.worked_by_name || 'Unknown'}</TableCell>
                                <TableCell align="right">{u.total_minutes || 0}</TableCell>
                              </TableRow>
                            ))}
                            {perUser.length === 0 && <TableRow><TableCell colSpan={2} align="center">No logs</TableCell></TableRow>}
                          </TableBody>
                        </Table>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Assignment History</Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow><TableCell>From</TableCell><TableCell>To</TableCell><TableCell>Date</TableCell></TableRow>
                          </TableHead>
                          <TableBody>
                            {assignmentHistory.map(a => (
                              <TableRow key={a.id}>
                                <TableCell>{a.previous_assignee_name || 'Unassigned'}</TableCell>
                                <TableCell>{a.new_assignee_name || 'Unassigned'}</TableCell>
                                <TableCell>{new Date(a.changed_at).toLocaleDateString()}</TableCell>
                              </TableRow>
                            ))}
                            {assignmentHistory.length === 0 && <TableRow><TableCell colSpan={3} align="center">No assignment history</TableCell></TableRow>}
                          </TableBody>
                        </Table>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

